import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { environment } from '../../environments/environment';
import { BatchPickState, IssueDetail, IssueKind } from './models/batchpick-state';
import { PickScanResult, VerifyScanResult } from './models/scan-result';

interface ApiEnvelope<T> {
  code?: number;
  message?: string;
  result: T;
  details?: any;
}

@Injectable({ providedIn: 'root' })
export class BatchPickService {
  private readonly base = `${environment.apiUrl}v1/BatchPick`;

  constructor(private http: HttpClient) {}

  private unwrap<T>(o$: Observable<ApiEnvelope<T>>): Observable<T> {
    return o$.pipe(map((r) => r.result));
  }

  state(batchId: string): Observable<BatchPickState> {
    return this.unwrap(this.http.get<ApiEnvelope<BatchPickState>>(`${this.base}/${batchId}`));
  }

  startPick(batchId: string, basketStartNumber = 1): Observable<unknown> {
    return this.http.post(`${this.base}/${batchId}/start-pick`, { basketStartNumber });
  }

  pickScan(batchId: string, barcode: string): Observable<PickScanResult> {
    return this.unwrap(
      this.http.post<ApiEnvelope<PickScanResult>>(`${this.base}/${batchId}/pick-scan`, { barcode }),
    );
  }

  finishPick(batchId: string): Observable<unknown> {
    return this.http.post(`${this.base}/${batchId}/finish-pick`, {});
  }

  startVerify(batchId: string): Observable<unknown> {
    return this.http.post(`${this.base}/${batchId}/start-verify`, {});
  }

  verifyScan(batchId: string, basket: number, barcode: string): Observable<VerifyScanResult> {
    return this.unwrap(
      this.http.post<ApiEnvelope<VerifyScanResult>>(
        `${this.base}/${batchId}/basket/${basket}/verify-scan`,
        { barcode },
      ),
    );
  }

  moveNow(
    batchId: string,
    basket: number,
    medicationId: string,
    fromBasket: number,
    toBasket: number,
  ): Observable<unknown> {
    return this.http.post(`${this.base}/${batchId}/basket/${basket}/move-now`, {
      medicationId,
      fromBasket,
      toBasket,
    });
  }

  deferIssue(
    batchId: string,
    body: {
      kind: IssueKind;
      medicationId?: string;
      scannedProductId: string;
      scannedInBasket?: number;
      expectedBasket?: number;
    },
  ): Observable<unknown> {
    return this.http.post(`${this.base}/${batchId}/defer-issue`, body);
  }

  issues(batchId: string): Observable<IssueDetail[]> {
    return this.unwrap(
      this.http.get<ApiEnvelope<IssueDetail[]>>(`${this.base}/${batchId}/issues`),
    );
  }

  resolveIssue(batchId: string, issueId: number, resolutionNote: string): Observable<unknown> {
    return this.http.post(`${this.base}/${batchId}/issue/${issueId}/resolve`, { resolutionNote });
  }

  finishVerify(batchId: string): Observable<unknown> {
    return this.http.post(`${this.base}/${batchId}/finish-verify`, {});
  }
}
